import { Checkbox } from '@any-ui-react/core';
import classNames from 'classnames';
import { type Dispatch, type Key, type ReactNode, type SetStateAction, useCallback, useEffect, useState } from 'react';

interface Props<T, S> {
  defaultValue: Array<S>;
  direction: 'row' | 'column';
  disabled: (item: T) => boolean;
  items: Array<T>;
  label: (item: T) => ReactNode;
  reactKey: (item: T) => Key;
  selectedKey: (item: T) => S;
  onChange: Dispatch<SetStateAction<Array<S>>>;
  error?: string;
}

export const MultiSelectChip = <T, S>({
  defaultValue,
  direction,
  disabled,
  items,
  label,
  reactKey,
  selectedKey,
  onChange,
  error,
}: Props<T, S>) => {
  const [selected, setSelected] = useState<Array<S>>(defaultValue);

  useEffect(() => {
    onChange(selected);
  }, [onChange, selected]);

  const onCheckboxChange = useCallback(
    (item: T) => {
      const key = selectedKey(item);

      setSelected(prev => {
        if (prev.some(it => it === key)) {
          return prev.filter(it => it !== key);
        }

        return [...prev, key];
      });
    },
    [selectedKey],
  );

  return (
    <div>
      <div className={classNames('inline-flex flex-wrap gap-2 items-start', { 'flex-col': direction === 'column' })}>
        {items.map(item => {
          const isChecked = selected.includes(selectedKey(item));
          const isDisabled = disabled(item);

          return (
            <div
              key={reactKey(item)}
              className={classNames('border rounded px-4 py-2 select-none transition-colors', {
                'cursor-pointer': !isDisabled,
                'cursor-not-allowed': isDisabled,
                'border-blue-6 bg-blue-100': isChecked && !error,
                'border-red-6 bg-red-50': error,
                'hover:border-gray-400': !isChecked && !isDisabled,
                'bg-gray-100': isDisabled,
              })}
              onClick={() => !isDisabled && onCheckboxChange(item)}
            >
              <div className="pointer-events-none">
                <Checkbox
                  checked={isChecked}
                  classNames={{
                    label: classNames({ 'text-blue-6': isChecked }),
                  }}
                  disabled={isDisabled}
                  label={label(item)}
                  onChange={() => void 0}
                />
              </div>
            </div>
          );
        })}
      </div>
      {error && <p className="text-red-6 mt-1 text-xs">{error}</p>}
    </div>
  );
};
